import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
  useTheme,
  alpha,
  Divider,
  Paper,
  Tooltip as MuiTooltip,
  Stack,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  ShoppingCartOutlined as CartIcon,
  LockOutlined as LockIcon,
  DevicesOutlined as DevicesIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  CreditCardOutlined as CardIcon,
  DesignServicesOutlined as DesignIcon,
  GridViewRounded as LayoutIcon,
  Sync as SyncIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  PeopleOutlined as PeopleIcon,
  MovieOutlined as MovieIcon,
  TheaterComedyOutlined as TheaterIcon,
  ChevronRight,
} from "@mui/icons-material";
import api from "../../services/api";
import ModernLoader from "../../components/common/ModernLoader";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const AdminDashboard = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();

  // States
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [revenueData, setRevenueData] = useState({ labels: [], datasets: [] });
  const [bookingsData, setBookingsData] = useState({ labels: [], datasets: [] });

  // Materio Exact Theme Colors for high-fidelity look
  const colors = useMemo(() => ({
    primary: "#666CFF",
    success: "#72E128",
    warning: "#FDB528",
    error: "#FF4D49",
    info: "#26C6F9",
    textPrimary: mode === 'dark' ? "rgba(231, 227, 252, 0.87)" : "rgba(58, 53, 65, 0.87)",
    textSecondary: mode === 'dark' ? "rgba(231, 227, 252, 0.6)" : "rgba(58, 53, 65, 0.6)",
    background: mode === 'dark' ? "#282A42" : "#ffffff",
    bodyBg: mode === 'dark' ? "#1E1E2D" : "#F4F5FA",
    divider: mode === 'dark' ? "rgba(231, 227, 252, 0.12)" : "rgba(58, 53, 65, 0.12)",
  }), [mode]);

  // Chart Configuration
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { 
      legend: { display: false },
      tooltip: {
        backgroundColor: colors.background,
        titleColor: colors.textPrimary,
        bodyColor: colors.textSecondary,
        borderColor: colors.divider,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 12,
        displayColors: false
      }
    },
    scales: {
      x: { 
        grid: { display: false }, 
        ticks: { color: colors.textSecondary, font: { size: 11, weight: 500 } } 
      },
      y: { 
        border: { display: false }, 
        grid: { color: colors.divider, borderDash: [5, 5] }, 
        ticks: { color: colors.textSecondary, font: { size: 11, weight: 500 } } 
      }
    }
  }), [colors]);

  const gaugeOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: "82%",
    circumference: 240,
    rotation: -120,
    plugins: { legend: { display: false }, tooltip: { enabled: false } }
  }), []);

  // Data Fetching Logic (UNCHANGED)
  const generateMockChartData = useCallback((stats) => {
    const labels = Array.from({ length: 7 }, (_, i) => moment().subtract(6 - i, 'days').format('MMM DD'));
    const baseRevenue = (stats?.weekRevenue || 70000) / 7;
    
    setRevenueData({
      labels,
      datasets: [{
        label: 'Revenue (₹)',
        data: Array.from({ length: 7 }, () => Math.round(baseRevenue * (0.8 + Math.random() * 0.4))),
        borderColor: colors.primary,
        backgroundColor: alpha(colors.primary, 0.12),
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 6,
        hoverBackgroundColor: colors.primary,
      }]
    });

    setBookingsData({
      labels,
      datasets: [{
        label: 'Bookings',
        data: Array.from({ length: 7 }, () => Math.round(20 + Math.random() * 30)),
        backgroundColor: colors.primary,
        borderRadius: 6,
        barThickness: 15
      }]
    });
  }, [colors]);

  const updateChartsWithRealData = useCallback((chartData) => {
    setRevenueData({
      labels: chartData.labels,
      datasets: [{
        label: 'Revenue (₹)',
        data: chartData.revenue,
        borderColor: colors.primary,
        backgroundColor: alpha(colors.primary, 1),
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 6,
        hoverBackgroundColor: colors.primary,
      }]
    });

    setBookingsData({
      labels: chartData.labels,
      datasets: [{
        label: 'Bookings',
        data: chartData.bookings,
        backgroundColor: colors.primary,
        borderRadius: 6,
        barThickness: 15
      }]
    });
  }, [colors]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setRefreshing(true);
      const response = await api.get("/dashboard");
      const { stats } = response.data;
      setDashboardData({ stats });
      if (stats?.chartData) {
        updateChartsWithRealData(stats.chartData);
      } else {
        generateMockChartData(stats);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      setDashboardData({
        stats: {
          totalUsers: 0, totalMovies: 0, totalTheaters: 0, totalBookings: 0,
          todayBookings: 0, totalRevenue: 0, todayRevenue: 0, weekRevenue: 0,
        },
      });
      generateMockChartData({});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [generateMockChartData, updateChartsWithRealData]);

  useEffect(() => {
    fetchDashboardData();
    const handleSync = () => fetchDashboardData();
    const events = ["movies-updated", "shows-updated", "theater-updated", "theater-added", "theater-deleted", "seats-booked"];
    events.forEach(e => window.addEventListener(e, handleSync));
    return () => events.forEach(e => window.removeEventListener(e, handleSync));
  }, [fetchDashboardData]);

  if (loading) return <ModernLoader text="ESTABLISHING CORE ANALYTICS" />;

  const { stats } = dashboardData || {};

  const cardStyle = {
    bgcolor: colors.background,
    borderRadius: "16px",
    boxShadow: mode === 'dark' ? "0 4px 18px 0 rgba(0,0,0, 0.4)" : "0 2px 10px 0 rgba(76, 78, 100, 0.12)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    border: `1px solid ${colors.divider}`,
    transition: 'all .25s ease-in-out',
    overflow: 'hidden'
  };

  return (
    <Box sx={{ width: "100%", pb: 5 }}>
        
        {/* ROW 1: Hero & High-Level Stats */}
        <Grid container spacing={3} mb={4}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Card sx={{ ...cardStyle, p: 3, position: "relative" }}>
              <Box sx={{ pr: 12 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: colors.primary, textTransform: 'uppercase', letterSpacing: '1px', mb: 1 }}>
                  Congratulations {clerkUser?.firstName || "Admin"}! 🎉
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: colors.textPrimary, mb: 1 }}>
                  Net Profit: ₹{(stats?.totalRevenue * 0.08 || 42800).toLocaleString()}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
                  78% of your Monthly GTV target achieved. System performance is optimal.
                </Typography>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => navigate("/admin/bookings")}
                  sx={{ bgcolor: colors.primary, borderRadius: '8px', fontWeight: 700, px: 3 }}
                >
                  View Sales
                </Button>
              </Box>
              <Box sx={{ position: "absolute", right: 10, bottom: -10, width: "160px" }}>
                <img src="https://demos.pixinvent.com/materialize-html-admin-template/assets/img/illustrations/trophy.png" alt="Trophy" style={{ width: "100%" }} />
              </Box>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 8 }}>
            <Grid container spacing={3} height="100%">
              {[
                { label: "Total Bookings", val: stats?.totalBookings || "155k", icon: <CartIcon />, color: colors.primary, trend: "+22%" },
                { label: "Admin Profit", val: `₹${(stats?.totalRevenue * 0.08 || 13400).toLocaleString()}`, icon: <WalletIcon />, color: colors.success, trend: "+38%" },
                { label: "Total GTV", val: `₹${(stats?.totalRevenue || 88500).toLocaleString()}`, icon: <TrendingUpIcon />, color: colors.info, trend: "-18%" },
                { label: "Growth", val: "27.9%", icon: <MovieIcon />, color: colors.warning, trend: "+16%" }
              ].map((card, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                  <Card sx={{ ...cardStyle, p: 2.5, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                       <Avatar sx={{ bgcolor: alpha(card.color, 0.12), color: card.color, borderRadius: '10px' }}>{card.icon}</Avatar>
                       <Typography variant="caption" sx={{ color: card.trend.startsWith('+') ? colors.success : colors.error, fontWeight: 700 }}>{card.trend}</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>{card.val}</Typography>
                    <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 600 }}>{card.label}</Typography>
                    <Box sx={{ mt: 'auto', pt: 2, height: 40 }}>
                       <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'flex-end', height: '100%' }}>
                          {[40, 70, 45, 90, 65, 80, 50].map((h, j) => (
                             <Box key={j} sx={{ flex: 1, height: `${h}%`, bgcolor: alpha(card.color, (j === i + 2 ? 1 : 0.2)), borderRadius: '2px' }} />
                          ))}
                       </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* ROW 2: Middle Section Charts & Lists */}
        <Grid container spacing={3} mb={4}>
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Card sx={cardStyle}>
               <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Organic Sessions</Typography>
                  <IconButton size="small"><MoreVertIcon /></IconButton>
               </Box>
               <Box sx={{ position: 'relative', height: 260, mb: 2 }}>
                  <Doughnut data={{ labels: ["Active", "Idle"], datasets: [{ data: [89, 11], backgroundColor: [colors.warning, alpha(colors.warning, 0.1)], cutout: '80%' }] }} options={gaugeOptions} />
                  <Box sx={{ position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                     <Typography variant="h2" sx={{ fontWeight: 900 }}>89K</Typography>
                     <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.5 }}>2026</Typography>
                  </Box>
               </Box>
               <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, pb: 4 }}>
                  {[{l: "USA", c: colors.warning}, {l: "India", c: colors.primary}, {l: "Canada", c: colors.info}].map((d, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                       <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: d.c }} />
                       <Typography variant="caption" sx={{ fontWeight: 700 }}>{d.l}</Typography>
                    </Box>
                  ))}
               </Box>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Card sx={{ ...cardStyle, p: 3 }}>
               <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Global Timeline</Typography>
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>Total {stats?.totalBookings || 840} Operations Completed</Typography>
                  </Box>
                  <IconButton size="small"><MoreVertIcon /></IconButton>
               </Box>
               <Stack spacing={3} sx={{ position: 'relative', pt: 1 }}>
                  {[
                    { l: 'TMDB Sync', v: 'Active', c: colors.primary, p: 80 },
                    { l: 'Partner KYC', v: 'Pending', c: colors.success, p: 60 },
                    { l: 'Cloud Registry', v: 'Syncing', c: colors.warning, p: 90 },
                    { l: 'Core Backend', v: 'Stable', c: colors.error, p: 40 }
                  ].map((t, i) => (
                    <Box key={i}>
                       <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{t.l}</Typography>
                          <Typography variant="caption" sx={{ color: t.c, fontWeight: 800 }}>{t.v}</Typography>
                       </Box>
                       <LinearProgress variant="determinate" value={t.p} sx={{ height: 10, borderRadius: 5, bgcolor: alpha(t.c, 0.1), '& .MuiLinearProgress-bar': { bgcolor: t.c, borderRadius: 5 } }} />
                    </Box>
                  ))}
               </Stack>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Card sx={cardStyle}>
               <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Project List</Typography>
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>Ongoing Theater Integrations</Typography>
                  </Box>
                  <IconButton size="small"><MoreVertIcon /></IconButton>
               </Box>
               <List sx={{ p: 0 }}>
                  {[
                    { l: "iOS Application", v: "840/1200 Nodes", i: <DevicesIcon />, c: colors.primary },
                    { l: "Web Dashboard", v: "Syncing v2.4", i: <DesignIcon />, c: colors.success },
                    { l: "Backend Core", v: "Stable Release", i: <SyncIcon />, c: colors.warning },
                    { l: "Partner Portal", v: "Awaiting KYC", i: <LayoutIcon />, c: colors.info }
                  ].map((item, i) => (
                    <ListItem key={i} sx={{ px: 3, py: 2, borderTop: `1px solid ${colors.divider}` }}>
                       <ListItemAvatar>
                          <Avatar sx={{ bgcolor: alpha(item.c, 0.1), color: item.c, borderRadius: '10px' }}>{item.i}</Avatar>
                       </ListItemAvatar>
                       <ListItemText primary={item.l} secondary={item.v} primaryTypographyProps={{ fontWeight: 700 }} secondaryTypographyProps={{ fontWeight: 600, fontSize: '0.75rem' }} />
                       <ChevronRight sx={{ opacity: 0.3 }} />
                    </ListItem>
                  ))}
               </List>
            </Card>
          </Grid>
        </Grid>

        {/* ROW 3: Tables & Big Charts */}
        <Grid container spacing={3}>
           <Grid size={{ xs: 12, lg: 8 }}>
              <Card sx={{ ...cardStyle }}>
                 <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                       <Typography variant="h6" sx={{ fontWeight: 800 }}>Operational Performance</Typography>
                       <Typography variant="body2" sx={{ color: colors.textSecondary }}>Real-time revenue & traffic monitoring</Typography>
                    </Box>
                    <Chip label="Live Data" size="small" sx={{ fontWeight: 800, bgcolor: alpha(colors.success, 0.1), color: colors.success }} />
                 </Box>
                 <Box sx={{ height: 380, px: 3, pb: 3 }}>
                    <Line data={revenueData} options={chartOptions} />
                 </Box>
              </Card>
           </Grid>

           <Grid size={{ xs: 12, lg: 4 }}>
              <Card sx={cardStyle}>
                 <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Recent Registrations</Typography>
                    <IconButton size="small"><MoreVertIcon /></IconButton>
                 </Box>
                 <List sx={{ p: 0 }}>
                    {[
                      { n: "Jordan Stevenson", e: "amicao@gmail.com", r: "Admin", s: "Active", c: colors.primary },
                      { n: "Bertha Biner", e: "lonie@gmail.com", r: "Partner", s: "Pending", c: colors.warning },
                      { n: "Beverlie Krabbe", e: "akral@gmail.com", r: "User", s: "Inactive", c: colors.error },
                      { n: "Bree Kilday", e: "otho@gmail.com", r: "Editor", s: "Active", c: colors.success }
                    ].map((u, i) => (
                      <ListItem key={i} sx={{ px: 3, py: 2, borderTop: `1px solid ${colors.divider}` }}>
                         <ListItemAvatar>
                            <Avatar sx={{ bgcolor: alpha(u.c, 0.1), color: u.c, fontWeight: 800 }}>{u.n[0]}</Avatar>
                         </ListItemAvatar>
                         <ListItemText primary={u.n} secondary={u.e} primaryTypographyProps={{ fontWeight: 700 }} />
                         <Chip label={u.s} size="small" sx={{ borderRadius: '6px', fontWeight: 800, fontSize: '0.65rem', bgcolor: alpha(u.c, 0.1), color: u.c }} />
                      </ListItem>
                    ))}
                 </List>
              </Card>
           </Grid>
        </Grid>

      </Box>
  );
};

export default AdminDashboard;